import { z } from 'zod';

// ─── Tag normalization ────────────────────────────────────────────────────────

/** Lowercase, trim, replace whitespace with hyphens, max 50 chars */
export function normalizeTag(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, '-').slice(0, 50);
}

/** Tag format: 1-50 chars, lowercase alphanumeric + hyphen */
export const TagSchema = z
  .string()
  .min(1)
  .max(50)
  .regex(/^[a-z0-9а-яё][a-z0-9а-яё\-]*$/i, 'Tag must start with a letter or digit');

// ─── GET /admin/users (list + search) ────────────────────────────────────────

export const AdminUsersQuerySchema = z.object({
  q: z.string().max(200).optional(),
  tag: z.string().max(50).optional(),
  role: z.enum(['user', 'inhound', 'editor', 'admin']).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});
export type AdminUsersQuery = z.infer<typeof AdminUsersQuerySchema>;

export const AdminUserListItemSchema = z.object({
  id: z.string(),
  telegramUserId: z.string().nullable(),
  primaryEmail: z.string().nullable(),
  primaryPhone: z.string().nullable(),
  username: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  role: z.enum(['user', 'inhound', 'editor', 'admin']),
  identityProviders: z.array(z.enum(['telegram', 'supabase_email'])),
  identityState: z.enum(['telegram', 'email', 'telegram+email', 'import-only', 'no-auth']),
  matchedLegacyCustomerCount: z.number(),
  matchedLegacyOrderCount: z.number(),
  mergedIntoUserId: z.string().nullable(),
  mergedAt: z.string().nullable(),
  tags: z.array(z.string()),
  createdAt: z.string(),
  lastLoginAt: z.string().nullable(),
  entitlementCount: z.number(),
});
export type AdminUserListItem = z.infer<typeof AdminUserListItemSchema>;

export const AdminUsersResponseSchema = z.object({
  items: z.array(AdminUserListItemSchema),
  nextCursor: z.string().optional(),
});
export type AdminUsersResponse = z.infer<typeof AdminUsersResponseSchema>;

// ─── GET /admin/users/:userId (detail) ───────────────────────────────────────

export const AdminUserEntitlementSchema = z.object({
  id: z.string(),
  clubId: z.string(),
  clubTitle: z.string(),
  source: z.string(),
  createdAt: z.string(),
  revokedAt: z.string().nullable(),
});
export type AdminUserEntitlement = z.infer<typeof AdminUserEntitlementSchema>;

export const AdminAuditLogEntrySchema = z.object({
  id: z.string(),
  action: z.string(),
  actorUserId: z.string(),
  actorUsername: z.string().nullable(),
  metadata: z.record(z.unknown()).nullable(),
  createdAt: z.string(),
});
export type AdminAuditLogEntry = z.infer<typeof AdminAuditLogEntrySchema>;

export const AdminUserIdentitySchema = z.object({
  id: z.string(),
  provider: z.enum(['telegram', 'supabase_email']),
  providerSubject: z.string(),
  emailNormalized: z.string().nullable(),
  telegramUserId: z.string().nullable(),
  verifiedAt: z.string().nullable(),
  lastLoginAt: z.string().nullable(),
  createdAt: z.string(),
});
export type AdminUserIdentity = z.infer<typeof AdminUserIdentitySchema>;

export const AdminUserContactPointSchema = z.object({
  id: z.string(),
  type: z.enum(['email', 'phone', 'telegram']),
  valueRaw: z.string(),
  valueNormalized: z.string(),
  isPrimary: z.boolean(),
  isVerified: z.boolean(),
  source: z.enum(['auth', 'import', 'manual', 'payment', 'support']),
  createdAt: z.string(),
});
export type AdminUserContactPoint = z.infer<typeof AdminUserContactPointSchema>;

export const AdminUserMatchedLegacyCustomerSchema = z.object({
  id: z.string(),
  batchId: z.string(),
  batchSource: z.string(),
  sourceRecordId: z.string().nullable(),
  telegramId: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  name: z.string().nullable(),
  matchStatus: z.enum(['unmatched', 'matched', 'conflict', 'ignored']),
  createdAt: z.string(),
});
export type AdminUserMatchedLegacyCustomer = z.infer<typeof AdminUserMatchedLegacyCustomerSchema>;

export const AdminUserMatchedLegacyOrderSchema = z.object({
  id: z.string(),
  batchId: z.string(),
  batchSource: z.string(),
  sourceOrderId: z.string().nullable(),
  courseId: z.string().nullable(),
  paymentStatus: z.string().nullable(),
  paidAt: z.string().nullable(),
  matchStatus: z.enum(['unmatched', 'matched', 'conflict', 'ignored']),
  mappedClubId: z.string().nullable(),
  mappedClubTitle: z.string().nullable(),
  createdAt: z.string(),
});
export type AdminUserMatchedLegacyOrder = z.infer<typeof AdminUserMatchedLegacyOrderSchema>;

export const AdminUserMergeCandidateSchema = z.object({
  userId: z.string(),
  username: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  primaryEmail: z.string().nullable(),
  primaryPhone: z.string().nullable(),
  telegramUserId: z.string().nullable(),
  role: z.enum(['user', 'inhound', 'editor', 'admin']),
  identityState: z.enum(['telegram', 'email', 'telegram+email', 'import-only', 'no-auth']),
  reasons: z.array(z.string()),
  sharedSignals: z.array(z.string()),
  mergedIntoUserId: z.string().nullable(),
  mergedAt: z.string().nullable(),
});
export type AdminUserMergeCandidate = z.infer<typeof AdminUserMergeCandidateSchema>;

export const AdminUserReadingSummarySchema = z.object({
  clubsWithActiveAccessCount: z.number().int().nonnegative(),
  clubsStartedCount: z.number().int().nonnegative(),
  clubsReached25Count: z.number().int().nonnegative(),
  clubsReached50Count: z.number().int().nonnegative(),
  clubsReached80Count: z.number().int().nonnegative(),
  clubsReached100Count: z.number().int().nonnegative(),
  averageProgressPercent: z.number().nonnegative(),
  medianProgressPercent: z.number().nonnegative(),
  activeReadDays30d: z.number().int().nonnegative(),
  hasReadingInLast1d: z.boolean(),
  hasReadingInLast7d: z.boolean(),
  hasReadingInLast30d: z.boolean(),
  lastReadAt: z.string().nullable(),
  lastReadClubId: z.string().nullable(),
  lastReadClubTitle: z.string().nullable(),
  lastReadChapterId: z.string().nullable(),
  lastReadChapterTitle: z.string().nullable(),
});
export type AdminUserReadingSummary = z.infer<typeof AdminUserReadingSummarySchema>;

export const AdminUserReadingClubRowSchema = z.object({
  clubId: z.string(),
  clubTitle: z.string(),
  accessStatus: z.enum(['active', 'revoked', 'none']),
  accessGrantedAt: z.string().nullable(),
  accessRevokedAt: z.string().nullable(),
  firstReadAt: z.string().nullable(),
  lastReadAt: z.string().nullable(),
  progressPercent: z.number().nonnegative(),
  completedChapters: z.number().int().nonnegative(),
  totalPublishedChapters: z.number().int().nonnegative(),
  activeReadDays30d: z.number().int().nonnegative(),
  hasReadingInLast7d: z.boolean(),
  hasReadingInLast30d: z.boolean(),
  latestChapterId: z.string().nullable(),
  latestChapterTitle: z.string().nullable(),
});
export type AdminUserReadingClubRow = z.infer<typeof AdminUserReadingClubRowSchema>;

export const AdminUserReadingActivityEntrySchema = z.object({
  chapterId: z.string(),
  chapterTitle: z.string(),
  chapterIndex: z.number().int().nonnegative(),
  clubId: z.string(),
  clubTitle: z.string(),
  state: z.enum(['not_started', 'in_progress', 'completed']),
  scrollDepthMax: z.number().nonnegative(),
  activeTimeSeconds: z.number().int().nonnegative(),
  updatedAt: z.string(),
  completedAt: z.string().nullable(),
});
export type AdminUserReadingActivityEntry = z.infer<typeof AdminUserReadingActivityEntrySchema>;

export const AdminUserReadingInsightsSchema = z.object({
  summary: AdminUserReadingSummarySchema,
  clubRows: z.array(AdminUserReadingClubRowSchema),
  recentActivity: z.array(AdminUserReadingActivityEntrySchema),
});
export type AdminUserReadingInsights = z.infer<typeof AdminUserReadingInsightsSchema>;

export const AdminUserDetailSchema = z.object({
  id: z.string(),
  telegramUserId: z.string().nullable(),
  primaryEmail: z.string().nullable(),
  primaryPhone: z.string().nullable(),
  username: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  photoUrl: z.string().nullable(),
  role: z.enum(['user', 'inhound', 'editor', 'admin']),
  identityProviders: z.array(z.enum(['telegram', 'supabase_email'])),
  identityState: z.enum(['telegram', 'email', 'telegram+email', 'import-only', 'no-auth']),
  mergedIntoUserId: z.string().nullable(),
  mergedAt: z.string().nullable(),
  hasAdminCredential: z.boolean(),
  createdAt: z.string(),
  lastLoginAt: z.string().nullable(),
  tags: z.array(z.string()),
  identities: z.array(AdminUserIdentitySchema),
  contactPoints: z.array(AdminUserContactPointSchema),
  matchedLegacyCustomers: z.array(AdminUserMatchedLegacyCustomerSchema),
  matchedLegacyOrders: z.array(AdminUserMatchedLegacyOrderSchema),
  mergeCandidates: z.array(AdminUserMergeCandidateSchema),
  entitlements: z.array(AdminUserEntitlementSchema),
  readingInsights: AdminUserReadingInsightsSchema,
  auditLog: z.array(AdminAuditLogEntrySchema),
});
export type AdminUserDetail = z.infer<typeof AdminUserDetailSchema>;

// ─── PATCH /admin/users/:userId/role ─────────────────────────────────────────

export const AdminChangeRoleSchema = z.object({
  role: z.enum(['user', 'inhound', 'editor', 'admin']),
  comment: z.string().max(500).optional(),
});
export type AdminChangeRoleDto = z.infer<typeof AdminChangeRoleSchema>;

export const AdminUserMergePreviewRequestSchema = z.object({
  targetUserId: z.string().uuid(),
  sourceUserId: z.string().uuid(),
  preserveInhoundRole: z.boolean().default(false),
});
export type AdminUserMergePreviewRequest = z.infer<typeof AdminUserMergePreviewRequestSchema>;

export const AdminUserMergePreviewSummarySchema = z.object({
  identitiesToMove: z.number(),
  contactPointsToMove: z.number(),
  contactPointCollisions: z.number(),
  entitlementsToMove: z.number(),
  entitlementCollisions: z.number(),
  tagsToMove: z.number(),
  tagCollisions: z.number(),
  chapterProgressToMove: z.number(),
  chapterProgressCollisions: z.number(),
  mediaProgressToMove: z.number(),
  mediaProgressCollisions: z.number(),
  notificationLogsToMove: z.number(),
  notificationLogCollisions: z.number(),
  legacyCustomersToMove: z.number(),
  legacyOrdersToMove: z.number(),
  analyticsEventsToMove: z.number(),
});
export type AdminUserMergePreviewSummary = z.infer<typeof AdminUserMergePreviewSummarySchema>;

export const AdminUserMergePreviewParticipantSchema = z.object({
  id: z.string(),
  username: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  primaryEmail: z.string().nullable(),
  primaryPhone: z.string().nullable(),
  telegramUserId: z.string().nullable(),
  role: z.enum(['user', 'inhound', 'editor', 'admin']),
  identityState: z.enum(['telegram', 'email', 'telegram+email', 'import-only', 'no-auth']),
  hasAdminCredential: z.boolean(),
  mergedIntoUserId: z.string().nullable(),
  mergedAt: z.string().nullable(),
});
export type AdminUserMergePreviewParticipant = z.infer<typeof AdminUserMergePreviewParticipantSchema>;

export const AdminUserMergePreviewSchema = z.object({
  canMerge: z.boolean(),
  blockingReasons: z.array(z.string()),
  warnings: z.array(z.string()),
  effectiveRole: z.enum(['user', 'inhound', 'editor', 'admin']),
  targetUser: AdminUserMergePreviewParticipantSchema,
  sourceUser: AdminUserMergePreviewParticipantSchema,
  summary: AdminUserMergePreviewSummarySchema,
});
export type AdminUserMergePreview = z.infer<typeof AdminUserMergePreviewSchema>;

export const AdminUserMergeApplySchema = z.object({
  targetUserId: z.string().uuid(),
  sourceUserId: z.string().uuid(),
  preserveInhoundRole: z.boolean().default(false),
  confirm: z.literal(true),
});
export type AdminUserMergeApplyDto = z.infer<typeof AdminUserMergeApplySchema>;

export const AdminUserMergeApplyResultSchema = z.object({
  merged: z.boolean(),
  targetUserId: z.string(),
  sourceUserId: z.string(),
  mergedAt: z.string(),
  effectiveRole: z.enum(['user', 'inhound', 'editor', 'admin']),
  summary: AdminUserMergePreviewSummarySchema,
});
export type AdminUserMergeApplyResult = z.infer<typeof AdminUserMergeApplyResultSchema>;

// ─── POST /admin/entitlements/grant ──────────────────────────────────────────

export const AdminGrantEntitlementSchema = z.object({
  targetUserId: z.string().uuid(),
  clubId: z.string(),
  comment: z.string().max(500).optional(),
});
export type AdminGrantEntitlementDto = z.infer<typeof AdminGrantEntitlementSchema>;

export const AdminEntitlementResultSchema = z.object({
  id: z.string(),
  userId: z.string(),
  clubId: z.string(),
  source: z.string(),
  createdAt: z.string(),
  revokedAt: z.string().nullable(),
  alreadyExisted: z.boolean(),
});
export type AdminEntitlementResult = z.infer<typeof AdminEntitlementResultSchema>;

// ─── POST /admin/entitlements/revoke ─────────────────────────────────────────

export const AdminRevokeEntitlementSchema = z.object({
  targetUserId: z.string().uuid(),
  clubId: z.string(),
  comment: z.string().max(500).optional(),
});
export type AdminRevokeEntitlementDto = z.infer<typeof AdminRevokeEntitlementSchema>;

// ─── POST /admin/entitlements/bulk ───────────────────────────────────────────

export const AdminBulkEntitlementSchema = z.object({
  operation: z.enum(['grant', 'revoke']),
  clubId: z.string(),
  targetUserIds: z.array(z.string().uuid()).min(1).max(100),
  dryRun: z.boolean().default(false),
  comment: z.string().max(500).optional(),
});
export type AdminBulkEntitlementDto = z.infer<typeof AdminBulkEntitlementSchema>;

export const AdminBulkEntitlementResultItemSchema = z.object({
  userId: z.string(),
  status: z.enum(['success', 'skipped', 'error']),
  message: z.string().optional(),
});
export type AdminBulkEntitlementResultItem = z.infer<typeof AdminBulkEntitlementResultItemSchema>;

export const AdminBulkEntitlementResultSchema = z.object({
  dryRun: z.boolean(),
  operation: z.enum(['grant', 'revoke']),
  clubId: z.string(),
  results: z.array(AdminBulkEntitlementResultItemSchema),
  total: z.number(),
  success: z.number(),
  skipped: z.number(),
  errors: z.number(),
});
export type AdminBulkEntitlementResult = z.infer<typeof AdminBulkEntitlementResultSchema>;

// ─── POST /admin/users/tags/assign ───────────────────────────────────────────

export const AdminAssignTagSchema = z.object({
  targetUserIds: z.array(z.string().uuid()).min(1).max(100),
  tag: z.string().min(1).max(50),
});
export type AdminAssignTagDto = z.infer<typeof AdminAssignTagSchema>;

// ─── POST /admin/users/bulk-delete ───────────────────────────────────────────

export const AdminBulkDeleteUsersSchema = z.object({
  targetUserIds: z.array(z.string().uuid()).min(1).max(100),
});
export type AdminBulkDeleteUsersDto = z.infer<typeof AdminBulkDeleteUsersSchema>;

export const AdminBulkDeleteUsersResultItemSchema = z.object({
  userId: z.string(),
  status: z.enum(['success', 'skipped', 'error']),
  message: z.string().optional(),
});
export type AdminBulkDeleteUsersResultItem = z.infer<typeof AdminBulkDeleteUsersResultItemSchema>;

export const AdminBulkDeleteUsersResultSchema = z.object({
  results: z.array(AdminBulkDeleteUsersResultItemSchema),
  total: z.number(),
  success: z.number(),
  skipped: z.number(),
  errors: z.number(),
});
export type AdminBulkDeleteUsersResult = z.infer<typeof AdminBulkDeleteUsersResultSchema>;

// ─── POST /admin/users/import ────────────────────────────────────────────────

export const AdminImportUsersSchema = z.object({
  telegramIds: z.array(z.string().min(1).max(20)).min(1).max(500),
  tag: z.string().min(1).max(50).optional(),
  dryRun: z.boolean().default(false),
});
export type AdminImportUsersDto = z.infer<typeof AdminImportUsersSchema>;

export const AdminImportUsersResultItemSchema = z.object({
  telegramId: z.string(),
  status: z.enum(['created', 'updated', 'skipped', 'error']),
  message: z.string().optional(),
});
export type AdminImportUsersResultItem = z.infer<typeof AdminImportUsersResultItemSchema>;

export const AdminImportUsersResultSchema = z.object({
  dryRun: z.boolean(),
  results: z.array(AdminImportUsersResultItemSchema),
  total: z.number(),
  created: z.number(),
  updated: z.number(),
  skipped: z.number(),
  errors: z.number(),
});
export type AdminImportUsersResult = z.infer<typeof AdminImportUsersResultSchema>;

// ─── Legacy customer / order import foundation ─────────────────────────────

export const LegacyImportMatchStatusSchema = z.enum(['unmatched', 'matched', 'conflict', 'ignored']);
export type LegacyImportMatchStatus = z.infer<typeof LegacyImportMatchStatusSchema>;

export const AdminLegacyImportCustomerInputSchema = z.object({
  sourceRecordId: z.string().min(1).max(200).optional(),
  telegramId: z.string().min(1).max(100).optional(),
  email: z.string().email().optional(),
  phone: z.string().min(1).max(100).optional(),
  name: z.string().min(1).max(300).optional(),
  metadata: z.record(z.unknown()).optional(),
});
export type AdminLegacyImportCustomerInput = z.infer<typeof AdminLegacyImportCustomerInputSchema>;

export const AdminLegacyImportOrderInputSchema = z.object({
  sourceOrderId: z.string().min(1).max(200).optional(),
  customerSourceRecordId: z.string().min(1).max(200).optional(),
  courseId: z.string().min(1).max(200).optional(),
  paymentStatus: z.string().min(1).max(100).optional(),
  paidAt: z.string().datetime().optional(),
  metadata: z.record(z.unknown()).optional(),
});
export type AdminLegacyImportOrderInput = z.infer<typeof AdminLegacyImportOrderInputSchema>;

export const AdminLegacyImportBatchCreateSchema = z.object({
  source: z.string().min(1).max(100),
  notes: z.string().max(500).optional(),
  dryRun: z.boolean().default(false),
  customers: z.array(AdminLegacyImportCustomerInputSchema).max(5000).default([]),
  orders: z.array(AdminLegacyImportOrderInputSchema).max(10000).default([]),
  metadata: z.record(z.unknown()).optional(),
});
export type AdminLegacyImportBatchCreateDto = z.infer<typeof AdminLegacyImportBatchCreateSchema>;

export const AdminLegacyImportMatchDiagnosticSchema = z.object({
  exactSignalKinds: z.array(z.string()),
  exactMatchedUserIds: z.array(z.string()),
  phoneCandidateUserIds: z.array(z.string()),
});
export type AdminLegacyImportMatchDiagnostic = z.infer<typeof AdminLegacyImportMatchDiagnosticSchema>;

export const AdminLegacyImportCustomerPreviewSchema = z.object({
  sourceRecordId: z.string().nullable(),
  telegramId: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  name: z.string().nullable(),
  matchStatus: LegacyImportMatchStatusSchema,
  matchedUserId: z.string().nullable(),
  diagnostics: AdminLegacyImportMatchDiagnosticSchema,
  note: z.string().nullable(),
});
export type AdminLegacyImportCustomerPreview = z.infer<typeof AdminLegacyImportCustomerPreviewSchema>;

export const AdminLegacyImportOrderPreviewSchema = z.object({
  sourceOrderId: z.string().nullable(),
  customerSourceRecordId: z.string().nullable(),
  courseId: z.string().nullable(),
  paymentStatus: z.string().nullable(),
  paidAt: z.string().nullable(),
  matchStatus: LegacyImportMatchStatusSchema,
  matchedUserId: z.string().nullable(),
  mappedClubId: z.string().nullable(),
  mappedClubTitle: z.string().nullable(),
  note: z.string().nullable(),
});
export type AdminLegacyImportOrderPreview = z.infer<typeof AdminLegacyImportOrderPreviewSchema>;

export const AdminLegacyImportBatchCountsSchema = z.object({
  customers: z.number(),
  orders: z.number(),
  matchedCustomers: z.number(),
  conflictedCustomers: z.number(),
  unmatchedCustomers: z.number(),
  ignoredCustomers: z.number(),
  matchedOrders: z.number(),
  conflictedOrders: z.number(),
  unmatchedOrders: z.number(),
  ignoredOrders: z.number(),
});
export type AdminLegacyImportBatchCounts = z.infer<typeof AdminLegacyImportBatchCountsSchema>;

export const AdminLegacyImportBatchCreateResultSchema = z.object({
  dryRun: z.boolean(),
  batchId: z.string().nullable(),
  source: z.string(),
  notes: z.string().nullable(),
  counts: AdminLegacyImportBatchCountsSchema,
  grantedEntitlements: z.number(),
  customers: z.array(AdminLegacyImportCustomerPreviewSchema),
  orders: z.array(AdminLegacyImportOrderPreviewSchema),
});
export type AdminLegacyImportBatchCreateResult = z.infer<typeof AdminLegacyImportBatchCreateResultSchema>;

export const AdminLegacyImportBatchSummarySchema = z.object({
  id: z.string(),
  source: z.string(),
  notes: z.string().nullable(),
  createdAt: z.string(),
  createdByUserId: z.string().nullable(),
  counts: AdminLegacyImportBatchCountsSchema,
});
export type AdminLegacyImportBatchSummary = z.infer<typeof AdminLegacyImportBatchSummarySchema>;

export const AdminLegacyImportBatchListSchema = z.object({
  items: z.array(AdminLegacyImportBatchSummarySchema),
});
export type AdminLegacyImportBatchList = z.infer<typeof AdminLegacyImportBatchListSchema>;

export const AdminLegacyImportMatchedUserSchema = z.object({
  id: z.string(),
  username: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  telegramUserId: z.string().nullable(),
  primaryEmail: z.string().nullable(),
  role: z.enum(['user', 'inhound', 'editor', 'admin']),
});
export type AdminLegacyImportMatchedUser = z.infer<typeof AdminLegacyImportMatchedUserSchema>;

export const AdminLegacyCustomerRecordSchema = z.object({
  id: z.string(),
  sourceRecordId: z.string().nullable(),
  telegramId: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  name: z.string().nullable(),
  matchStatus: LegacyImportMatchStatusSchema,
  matchedUserId: z.string().nullable(),
  matchedUser: AdminLegacyImportMatchedUserSchema.nullable(),
  diagnostics: AdminLegacyImportMatchDiagnosticSchema,
  note: z.string().nullable(),
  createdAt: z.string(),
});
export type AdminLegacyCustomerRecord = z.infer<typeof AdminLegacyCustomerRecordSchema>;

export const AdminLegacyOrderRecordSchema = z.object({
  id: z.string(),
  sourceOrderId: z.string().nullable(),
  customerSourceRecordId: z.string().nullable(),
  courseId: z.string().nullable(),
  paymentStatus: z.string().nullable(),
  paidAt: z.string().nullable(),
  matchStatus: LegacyImportMatchStatusSchema,
  matchedUserId: z.string().nullable(),
  matchedUser: AdminLegacyImportMatchedUserSchema.nullable(),
  mappedClubId: z.string().nullable(),
  mappedClubTitle: z.string().nullable(),
  note: z.string().nullable(),
  createdAt: z.string(),
});
export type AdminLegacyOrderRecord = z.infer<typeof AdminLegacyOrderRecordSchema>;

export const AdminLegacyImportBatchDetailSchema = z.object({
  batch: AdminLegacyImportBatchSummarySchema,
  customers: z.array(AdminLegacyCustomerRecordSchema),
  orders: z.array(AdminLegacyOrderRecordSchema),
});
export type AdminLegacyImportBatchDetail = z.infer<typeof AdminLegacyImportBatchDetailSchema>;

export const AdminLegacyCourseProductMappingUpsertSchema = z.object({
  source: z.string().min(1).max(100),
  courseId: z.string().min(1).max(200),
  clubId: z.string().uuid().nullable().optional(),
  productLabel: z.string().max(200).optional(),
  notes: z.string().max(500).optional(),
  metadata: z.record(z.unknown()).optional(),
});
export type AdminLegacyCourseProductMappingUpsertDto = z.infer<typeof AdminLegacyCourseProductMappingUpsertSchema>;

export const AdminLegacyCourseProductMappingSchema = z.object({
  id: z.string(),
  source: z.string(),
  courseId: z.string(),
  clubId: z.string().nullable(),
  clubTitle: z.string().nullable(),
  productLabel: z.string().nullable(),
  notes: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AdminLegacyCourseProductMapping = z.infer<typeof AdminLegacyCourseProductMappingSchema>;

export const AdminLegacyCourseProductMappingListSchema = z.object({
  items: z.array(AdminLegacyCourseProductMappingSchema),
});
export type AdminLegacyCourseProductMappingList = z.infer<typeof AdminLegacyCourseProductMappingListSchema>;
